import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface FAQItem {
  question: string;
  answer: string;
}

export default function FAQSection() {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: 'A IA realmente consegue entender meus clientes?',
      answer: 'Sim! Nossa IA é treinada com milhões de conversas reais e entende gírias, abreviações, erros de digitação e até áudios. Ela interpreta a intenção do cliente e responde de forma natural, como um atendente humano experiente faria.',
    },
    {
      question: 'Quanto tempo leva para configurar a NeuroAgents?',
      answer: 'A configuração inicial leva apenas algumas horas. Nossa equipe cuida de toda a integração com seu WhatsApp Business e personaliza as respostas de acordo com seu negócio. Em até 24 horas sua IA já está funcionando.',
    },
    {
      question: 'E se a IA não souber responder alguma pergunta?',
      answer: 'A IA é inteligente o suficiente para reconhecer quando não tem certeza. Nesses casos, ela transfere automaticamente para um atendente humano ou agenda um retorno, garantindo que nenhum cliente fique sem resposta.',
    },
    {
      question: 'Posso personalizar as respostas da IA?',
      answer: 'Totalmente! Você define o tom de voz, as informações sobre produtos/serviços, horários de funcionamento, políticas de preço e muito mais. A IA se adapta 100% à identidade do seu negócio.',
    },
    {
      question: 'A NeuroAgents funciona com qual tipo de negócio?',
      answer: 'Atendemos mais de 500 empresas de diversos segmentos: barbearias, clínicas, imobiliárias, academias, restaurantes, e-commerces, consultórios, escritórios de advocacia, salões de beleza e muito mais.',
    },
    {
      question: 'Meus dados e conversas estão seguros?',
      answer: 'Absolutamente. Utilizamos criptografia de ponta a ponta e seguimos todas as normas da LGPD. Seus dados e de seus clientes estão protegidos com os mais altos padrões de segurança do mercado.',
    },
    {
      question: 'Posso cancelar a qualquer momento?',
      answer: 'Sim, não temos fidelidade. Você pode cancelar quando quiser, sem multas ou burocracia. Além disso, oferecemos garantia de 7 dias: se não economizar pelo menos 5 horas semanais, devolvemos seu dinheiro.',
    },
    {
      question: 'A IA consegue fazer agendamentos automaticamente?',
      answer: 'Sim! A IA se integra com sua agenda (Google Calendar, Calendly, etc.) e agenda compromissos automaticamente, verificando disponibilidade, enviando confirmações e lembretes aos clientes.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-16 sm:py-24 bg-dark">
      <div ref={sectionRef} className="max-w-4xl mx-auto px-4 sm:px-6">
        <div
          className={`text-center mb-10 sm:mb-16 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gradient-to-r from-secondary/10 to-accent-purple/10 border border-secondary/30 rounded-full mb-4 sm:mb-6">
            <i className="ri-question-answer-line text-secondary-light text-sm sm:text-base"></i>
            <span className="text-xs sm:text-sm text-secondary-light font-medium">Tire suas dúvidas</span>
          </div>
          <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            Perguntas <span className="text-transparent bg-clip-text bg-gradient-to-r from-secondary to-accent-purple">Frequentes</span>
          </h2>
          <p className="text-sm sm:text-lg text-gray-400 max-w-2xl mx-auto px-2">
            Tudo que você precisa saber sobre a NeuroAgents antes de começar.
          </p>
        </div>

        <div className="space-y-3 sm:space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className={`bg-dark-secondary border border-dark-border rounded-lg sm:rounded-xl overflow-hidden transition-all duration-700 ease-out hover:border-secondary/30 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              } ${openIndex === index ? 'border-secondary/50 shadow-lg shadow-secondary/5' : ''}`}
              style={{ transitionDelay: `${100 + index * 60}ms` }}
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full flex items-center justify-between p-4 sm:p-6 text-left cursor-pointer group"
              >
                <span className="text-sm sm:text-lg font-semibold pr-3 sm:pr-4 group-hover:text-secondary-light transition-colors">
                  {faq.question}
                </span>
                <div
                  className={`w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg transition-all duration-300 flex-shrink-0 ${
                    openIndex === index
                      ? 'bg-gradient-to-r from-secondary to-accent-purple text-white rotate-180'
                      : 'bg-dark-tertiary text-gray-400 group-hover:bg-secondary/20 group-hover:text-secondary-light'
                  }`}
                >
                  <i className="ri-arrow-down-s-line text-lg sm:text-xl"></i>
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ease-out ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-4 sm:px-6 pb-4 sm:pb-6">
                  <div className="h-px bg-dark-border mb-3 sm:mb-4"></div>
                  <p className="text-gray-400 text-xs sm:text-base leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className={`mt-8 sm:mt-12 text-center transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '700ms' }}
        >
          <p className="text-gray-400 text-sm sm:text-base mb-3 sm:mb-4">Ainda tem dúvidas?</p>
          <a
            href="https://wa.me/5541992584018?text=Olá! Tenho uma dúvida sobre a NeuroAgents"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 sm:px-6 py-2.5 sm:py-3 bg-gradient-to-r from-primary to-accent-cyan hover:opacity-90 text-dark font-bold rounded-lg transition-all hover:scale-105 whitespace-nowrap cursor-pointer text-sm sm:text-base"
          >
            <i className="ri-whatsapp-line text-lg sm:text-xl"></i>
            Fale com nosso time
          </a>
        </div>
      </div>
    </section>
  );
}
